import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
    DollarSign,
    TrendingUp,
    AlertTriangle,
    CreditCard,
    ArrowUpRight,
    ArrowDownRight,
    Activity,
    Brain,
    Target,
    Zap,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Loading } from '../components/ui/Loading';
import { TrialBanner } from '../components/banners/TrialBanner';
import { UsageWarningBanner } from '../components/banners/UsageWarningBanner';
import { UsageLimitBanner } from '../components/banners/UsageLimitBanner';
import { useDashboardOverview, useDashboardTrends } from '../hooks/useDashboard';
import { useIntelligenceStats, useOrgSummary } from '../hooks/useIntelligence';
import { formatCurrency } from '../lib/utils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const Dashboard: React.FC = () => {
    const { data: overview, isLoading: overviewLoading } = useDashboardOverview();
    const { data: trends, isLoading: trendsLoading } = useDashboardTrends(30, 'daily');
    const { data: intelligence } = useIntelligenceStats();
    const { data: orgSummary } = useOrgSummary(); // Removed error handling - handled by interceptor

    // Debug logs (optional - remove in production)
    // console.log('Dashboard Overview:', overview);
    // console.log('Dashboard Trends:', trends);
    // console.log('Trends Data Length:', trends?.data?.length || 0);

    if (overviewLoading) {
        return <Loading fullScreen message="Loading dashboard..." />;
    }

    // Calculate percentage changes using trends data (first vs last period)
    const calculateTrendChange = (firstValue: number, lastValue: number): { percentage: string; positive: boolean } | null => {
        // If first value is invalid, return null
        if (!firstValue || firstValue === 0) return null;

        // If values are exactly the same, show 0%
        if (firstValue === lastValue) {
            return { percentage: '0.0%', positive: true };
        }

        const change = ((lastValue - firstValue) / firstValue) * 100;

        // Cap extreme values for display (but don't reject them)
        const displayChange = Math.max(-99.9, Math.min(999.9, change));

        const positive = displayChange >= 0;
        const percentage = `${positive ? '+' : ''}${displayChange.toFixed(1)}%`;

        return { percentage, positive };
    };

    // Get first and last periods from trends data for comparison
    const trendsData = trends?.data || [];
    const hasMultiplePeriods = trendsData.length > 1;

    // Calculate changes - will be null if insufficient data
    let revenueChange = null;
    let lossChange = null;
    let paymentsChange = null;
    let healthChange = null;

    if (hasMultiplePeriods) {
        // Compare first and last period from trends
        const firstPeriod = trendsData[0];
        const lastPeriod = trendsData[trendsData.length - 1];

        revenueChange = calculateTrendChange(firstPeriod.total_amount, lastPeriod.total_amount);
        lossChange = calculateTrendChange(firstPeriod.estimated_loss, lastPeriod.estimated_loss);
        paymentsChange = calculateTrendChange(firstPeriod.total, lastPeriod.total);
        healthChange = calculateTrendChange(firstPeriod.health_rate, lastPeriod.health_rate);
    } else if (overview?.today && overview?.this_month) {
        // Single period: Since today = this_month (backend limitation),
        // we'll show static small changes to indicate activity
        // This happens when there's only 1 day of data

        // Use simple mock changes since we don't have historical comparison
        revenueChange = { percentage: '+0.0%', positive: true };
        lossChange = { percentage: '+0.0%', positive: true };
        paymentsChange = { percentage: '+0.0%', positive: true };
        healthChange = { percentage: '0.0%', positive: true };
    }

    // For loss, decrease is good (invert the positive flag)
    const lossChangeAdjusted = lossChange ? {
        percentage: lossChange.percentage,
        positive: !lossChange.positive,
    } : null;

    const stats = [
        {
            label: 'Total Revenue',
            value: formatCurrency(overview?.this_month?.total_amount || 0),
            icon: DollarSign,
            change: revenueChange?.percentage || null,
            positive: revenueChange?.positive ?? true,
            color: 'blue',
        },
        {
            label: 'Lost Revenue',
            value: formatCurrency(overview?.this_month?.estimated_loss || 0),
            icon: TrendingUp,
            change: lossChangeAdjusted?.percentage || null,
            positive: lossChangeAdjusted?.positive ?? true,
            color: 'red',
        },
        {
            label: 'Total Payments',
            value: overview?.this_month?.payments?.toLocaleString() || '0',
            icon: CreditCard,
            change: paymentsChange?.percentage || null,
            positive: paymentsChange?.positive ?? true,
            color: 'green',
        },
        {
            label: 'Health Score',
            value: `${overview?.health_score || 0}%`,
            icon: Activity,
            change: healthChange?.percentage || null,
            positive: healthChange?.positive ?? true,
            color: 'purple',
        },
    ];

    const riskStats = [
        { label: 'Critical', count: overview?.risks?.critical || 0, color: 'severity-critical' },
        { label: 'Unresolved', count: overview?.risks?.unresolved || 0, color: 'severity-high' },
        { label: 'Healthy', count: overview?.this_month?.healthy || 0, color: 'severity-low' },
        { label: 'Lost', count: overview?.this_month?.lost || 0, color: 'severity-medium' },
    ];

    return (
        <div className="space-y-6">
            {/* Warning Banners */}
            <UsageLimitBanner />
            <TrialBanner />
            <UsageWarningBanner />

            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
                <p className="text-gray-600 mt-1">Monitor your payment safety in real-time</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat) => {
                    const Icon = stat.icon;
                    return (
                        <Card key={stat.label} className="relative overflow-hidden">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                                    <h3 className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</h3>
                                    {stat.change ? (
                                        <div className="flex items-center gap-1 mt-2">
                                            {stat.positive ? (
                                                <ArrowUpRight className="w-4 h-4 text-green-600" />
                                            ) : (
                                                <ArrowDownRight className="w-4 h-4 text-red-600" />
                                            )}
                                            <span className={`text-sm font-medium ${stat.positive ? 'text-green-600' : 'text-red-600'}`}>
                                                {stat.change}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="mt-2">
                                            <span className="text-xs text-gray-400">No trend data yet</span>
                                        </div>
                                    )}
                                </div>
                                <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                                    <Icon className={`w-6 h-6 text-${stat.color}-600`} />
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue Trend */}
                <Card>
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
                        <Badge>Last 30 days</Badge>
                    </div>
                    {trendsLoading ? (
                        <Loading />
                    ) : (
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={trends?.data}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="period" stroke="#6b7280" fontSize={12} />
                                <YAxis stroke="#6b7280" fontSize={12} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                                />
                                <Line type="monotone" dataKey="total_amount" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} name="Revenue" />
                                <Line type="monotone" dataKey="estimated_loss" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="Lost" />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </Card>

                {/* Risk Distribution */}
                <Card>
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Risk Distribution</h3>
                        <Link to="/risks" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                            View All
                        </Link>
                    </div>
                    <div className="space-y-4">
                        {riskStats.map((risk) => (
                            <div key={risk.label} className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`w-3 h-3 rounded-full bg-${risk.color}`} />
                                    <span className="text-sm font-medium text-gray-700">{risk.label}</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{risk.count}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-6">
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={riskStats}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="label" stroke="#6b7280" fontSize={12} />
                                <YAxis stroke="#6b7280" fontSize={12} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                                />
                                <Bar dataKey="count" fill="#0ea5e9" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </div>

            {/* AI Intelligence Section */}
            {intelligence && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <Target className="w-5 h-5 text-orange-600" />
                            </div>
                            <h3 className="font-semibold text-gray-900">High Risk Payments</h3>
                        </div>
                        <p className="text-3xl font-bold text-orange-600 mt-2">
                            {intelligence.overview.high_risk_payments}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                            Avg {intelligence.overview.avg_risk_probability.toFixed(1)}% probability
                        </p>
                    </Card>

                    <Card>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Brain className="w-5 h-5 text-purple-600" />
                            </div>
                            <h3 className="font-semibold text-gray-900">Active Patterns</h3>
                        </div>
                        <p className="text-3xl font-bold text-purple-600 mt-2">
                            {intelligence.patterns.active}
                        </p>
                        {intelligence.patterns.unacknowledged > 0 && (
                            <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                {intelligence.patterns.unacknowledged} unacknowledged
                            </span>
                        )}
                    </Card>

                    <Card>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <Zap className="w-5 h-5 text-green-600" />
                            </div>
                            <h3 className="font-semibold text-gray-900">Preventable Loss</h3>
                        </div>
                        <p className="text-3xl font-bold text-green-600 mt-2">
                            ${intelligence.patterns.estimated_preventable_loss.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">per month</p>
                    </Card>
                </div>
            )}

            {/* AI Org Summary (Pro/Enterprise) */}
            {orgSummary && (
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                    <div className="flex items-center gap-2 mb-4">
                        <Brain className="w-6 h-6 text-blue-600" />
                        <h3 className="text-lg font-semibold text-blue-900">AI Weekly Summary</h3>
                        <span className="ml-auto px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">Pro</span>
                    </div>
                    <div className="prose prose-sm max-w-none text-gray-700">
                        <ReactMarkdown>{orgSummary.summary}</ReactMarkdown>
                    </div>
                    {orgSummary.recommendations && orgSummary.recommendations.length > 0 && (
                        <div className="mt-4 flex gap-2">
                            {orgSummary.recommendations.slice(0, 3).map((rec, idx) => (
                                <div
                                    key={idx}
                                    className="flex-1 p-3 bg-white/70 rounded-lg text-xs"
                                >
                                    <div className="font-medium text-gray-900 mb-1">{rec.action}</div>
                                    <div className="text-gray-600">{rec.effort} effort • {rec.impact} impact</div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="mt-4 text-xs text-blue-600">
                        Updated {new Date(orgSummary.generated_at).toLocaleString()}
                    </div>
                </Card>
            )}

            {/* Recent Activity */}
            <Card>
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Link
                        to="/payments"
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
                    >
                        <CreditCard className="w-8 h-8 text-primary-600 mb-3" />
                        <h4 className="font-semibold text-gray-900 mb-1">View Payments</h4>
                        <p className="text-sm text-gray-600">Browse all payment transactions</p>
                    </Link>
                    <Link
                        to="/risks"
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all"
                    >
                        <AlertTriangle className="w-8 h-8 text-red-600 mb-3" />
                        <h4 className="font-semibold text-gray-900 mb-1">Check Risks</h4>
                        <p className="text-sm text-gray-600">Review detected risk flags</p>
                    </Link>
                    <Link
                        to="/intelligence/patterns"
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all"
                    >
                        <Brain className="w-8 h-8 text-purple-600 mb-3" />
                        <h4 className="font-semibold text-gray-900 mb-1">AI Patterns</h4>
                        <p className="text-sm text-gray-600">Detected payment patterns</p>
                    </Link>
                    <Link
                        to="/settings/api-keys"
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all"
                    >
                        <TrendingUp className="w-8 h-8 text-green-600 mb-3" />
                        <h4 className="font-semibold text-gray-900 mb-1">API Keys</h4>
                        <p className="text-sm text-gray-600">Manage your integration keys</p>
                    </Link>
                </div>
            </Card>
        </div>
    );
};

export default Dashboard;
